import { ReceiptData } from '../../lib/stores/ReceiptStore';
import style from './style.module.scss';

interface ReceiptLayoutProps {
	data: ReceiptData;
}

export function ReceiptLayout({ data }: ReceiptLayoutProps) {
	return (
		<div className={style.receipt}>
			<header className={style.header}>
				<img className={style.header__logo} src={data.headerData.companyLogoPath} alt='Company Logo' />
				<h1 className={style.header__company}>{data.headerData.companyName}</h1>
				<p className={style.header__phone}>{data.headerData.companyPhone}</p>
				{data.headerData.extraInfo && (
					<div className={style.header__items}>
						{data.headerData.extraInfo.map(({ label, value, order, blankLineAfter }) => (
							<div
								key={order}
								className={[style['header-item'], blankLineAfter ? style['header-item--space-after'] : ''].join(' ')}
							>
								<span className={style['header-item__key']}>
									{label}
									{value ? ':' : ''}
								</span>
								{value && <span className={style['header-item__value']}>{value}</span>}
							</div>
						))}
					</div>
				)}
			</header>
			<ul className={style.body}>
				{data.items.length > 0 &&
					data.items.map((item, index) => {
						const hasFee = item.fee.value !== '$0.00';
						return (
						<li className={[style['body__item-group'], style['body-item-group']].join(' ')} key={index}>
							<ul className={[style['body-item-group__item'], style.item].join(' ')}>
								<li className={[style.item__kvp, style['item-kvp']].join(' ')}>
									<strong className={style['item-kvp__key']}>{item.datetime.label}:</strong>
									<span className={style['item-kvp__value']}>{item.datetime.value}</span>
								</li>
								<li className={[style.item__kvp, style['item-kvp']].join(' ')}>
									<strong className={style['item-kvp__key']}>{item.customerId.label}:</strong>
									<span className={style['item-kvp__value']}>{item.customerId.value}</span>
								</li>
								{item.invoiceNumber && (
									<li className={[style.item__kvp, style['item-kvp']].join(' ')}>
										<strong className={style['item-kvp__key']}>{item.invoiceNumber.label}:</strong>
										<span className={style['item-kvp__value']}>{item.invoiceNumber.value}</span>
									</li>
								)}
								{item.paymentNumber && (
									<li className={[style.item__kvp, style['item-kvp']].join(' ')}>
										<strong className={style['item-kvp__key']}>{item.paymentNumber.label}:</strong>
										<span className={style['item-kvp__value']}>{item.paymentNumber.value}</span>
									</li>
								)}
								<li className={[style.item__kvp, style['item-kvp']].join(' ')}>
									<strong className={style['item-kvp__key']}>{item.kioskNameOrId.label}:</strong>
									<span className={style['item-kvp__value']}>{item.kioskNameOrId.value}</span>
								</li>
								<li className={[style.item__kvp, style['item-kvp']].join(' ')}>
									<strong className={style['item-kvp__key']}>{item.paymentType.label}:</strong>
									<span className={style['item-kvp__value']}>{item.paymentType.value}</span>
								</li>
								<li className={[style.item__kvp, style['item-kvp']].join(' ')}>
									<strong className={style['item-kvp__key']}>{item.sessionNumber.label}:</strong>
									<span className={style['item-kvp__value']}>{item.sessionNumber.value}</span>
								</li>
								<li className={style.item__financial}>
									<table className={style['financial-summary']}>
										<tbody>
											{item.previousBalance && (
												<tr>
													<td className={style['financial-summary__key']}>{item.previousBalance.label}:</td>
													<td className={style['financial-summary__value']}>{item.previousBalance.value}</td>
												</tr>
											)}
											<tr>
												<td className={style['financial-summary__key']}>{item.amountPaid.label}:</td>
												<td className={style['financial-summary__value']}>{item.amountPaid.value}</td>
											</tr>
											{hasFee && (
												<tr>
													<td className={style['financial-summary__key']}>{item.fee.label}:</td>
													<td className={style['financial-summary__value']}>{item.fee.value}</td>
												</tr>
											)}
											{hasFee && (
												<tr>
													<td className={style['financial-summary__key']}>
														<strong>{item.total.label}:</strong>
													</td>
													<td className={style['financial-summary__value']}>{item.total.value}</td>
												</tr>
											)}
											{item.approximateAccountBalance && (
												<tr>
													<td className={style['financial-summary__key']}>
														<strong>{item.approximateAccountBalance.label}:</strong>
													</td>
													<td className={style['financial-summary__value']}>
														{item.approximateAccountBalance.value}
													</td>
												</tr>
											)}
										</tbody>
									</table>
								</li>
							</ul>
						</li>
						);
					})}
				{data.cashBoxItems.length > 0 &&
					data.cashBoxItems.map((item, index) => (
						<li className={[style['body__item-group'], style['body-item-group']].join(' ')} key={index}>
							<ul className={[style['body-item-group__item'], style.item].join(' ')}>
								<li className={[style.item__kvp, style['item-kvp']].join(' ')}>
									<strong className={style['item-kvp__key']}>{item.datetime.label}:</strong>
									<span className={style['item-kvp__value']}>{item.datetime.value}</span>
								</li>
								<li className={[style.item__kvp, style['item-kvp']].join(' ')}>
									<strong className={style['item-kvp__key']}>{item.kioskNameOrId.label}:</strong>
									<span className={style['item-kvp__value']}>{item.kioskNameOrId.value}</span>
								</li>
								<li className={[style.item__kvp, style['item-kvp'], style['item-kvp--space-after']].join(' ')}>
									<strong className={style['item-kvp__key']}>{item.employeeName.label}:</strong>
									<span className={style['item-kvp__value']}>{item.employeeName.value}</span>
								</li>
								<li className={[style.item__kvp, style['item-kvp'], style['item-kvp--right-aligned']].join(' ')}>
									<strong className={style['item-kvp__key']}>Ones:</strong>
									<span className={style['item-kvp__value']}>{item.ones}</span>
								</li>
								<li className={[style.item__kvp, style['item-kvp'], style['item-kvp--right-aligned']].join(' ')}>
									<strong className={style['item-kvp__key']}>Twos:</strong>
									<span className={style['item-kvp__value']}>{item.twos}</span>
								</li>
								<li className={[style.item__kvp, style['item-kvp'], style['item-kvp--right-aligned']].join(' ')}>
									<strong className={style['item-kvp__key']}>Fives:</strong>
									<span className={style['item-kvp__value']}>{item.fives}</span>
								</li>
								<li className={[style.item__kvp, style['item-kvp'], style['item-kvp--right-aligned']].join(' ')}>
									<strong className={style['item-kvp__key']}>Tens:</strong>
									<span className={style['item-kvp__value']}>{item.tens}</span>
								</li>
								<li className={[style.item__kvp, style['item-kvp'], style['item-kvp--right-aligned']].join(' ')}>
									<strong className={style['item-kvp__key']}>Twenties:</strong>
									<span className={style['item-kvp__value']}>{item.twenties}</span>
								</li>
								<li className={[style.item__kvp, style['item-kvp'], style['item-kvp--right-aligned']].join(' ')}>
									<strong className={style['item-kvp__key']}>Fifties:</strong>
									<span className={style['item-kvp__value']}>{item.fifties}</span>
								</li>
								<li className={[style.item__kvp, style['item-kvp'], style['item-kvp--right-aligned']].join(' ')}>
									<strong className={style['item-kvp__key']}>Hundreds:</strong>
									<span className={style['item-kvp__value']}>{item.hundreds}</span>
								</li>
								<li className={[style.item__kvp, style['item-kvp'], style['item-kvp--right-aligned']].join(' ')}>
									<strong className={style['item-kvp__key']}>Total Bills:</strong>
									<span className={style['item-kvp__value']}>{item.totalBills}</span>
								</li>
								<li className={[style.item__kvp, style['item-kvp'], style['item-kvp--right-aligned']].join(' ')}>
									<strong className={style['item-kvp__key']}>Total Cash:</strong>
									<span className={style['item-kvp__value']}>{item.totalCash}</span>
								</li>
							</ul>
						</li>
					))}
			</ul>
			<footer className={style.footer}>
				{data.footerData.extraInfo && (
					<div className={style.footer__items}>
						{data.footerData.extraInfo.map(({ label, value, blankLineAfter }, i) => (
							<div
								key={i}
								className={[style['footer-item'], blankLineAfter ? style['footer-item--space-after'] : ''].join(' ')}
							>
								<span className={style['footer-item__key']}>
									{label}
									{value ? ':' : ''}
								</span>
								<span className={style['footer-item__value']}>{value}</span>
							</div>
						))}
					</div>
				)}
				<img className={style.footer__logo} src={data.footerData.bpkLogoPath} alt='BPK Logo' />
			</footer>
		</div>
	);
}
